import React from 'react';
import { Box, Button, Icon, Text } from '@chakra-ui/react';
import { LuLock, LuLockOpen } from 'react-icons/lu';
import { Tooltip } from '@/components/ui/tooltip.tsx';
import { useHousePermissions } from '@/hooks/useHasPermission';
import { useAuth } from '@/context/AuthContext.tsx';
import {
	COMPOSER_BAR_CONTROL_H,
	COMPOSER_PILL_LABEL_MIN_WIDTH,
	COMPOSER_PILL_SHORT_MIN_WIDTH,
	COMPOSER_TOOLTIP_CLOSE_DELAY_MS,
	COMPOSER_TOOLTIP_OPEN_DELAY_MS,
	composerRowAtLeast,
} from './composerConstants.ts';

// Private loses its word first, then draws in the padding at the smaller threshold.
const PRIVACY_PILL_CSS = {
	paddingInline: '8px',
	gap: '4px',
	[composerRowAtLeast(COMPOSER_PILL_SHORT_MIN_WIDTH)]: { paddingInline: '12px', gap: '8px' },
} as const;
const PRIVACY_LABEL_CSS = {
	display: 'none',
	[composerRowAtLeast(COMPOSER_PILL_LABEL_MIN_WIDTH)]: { display: 'inline' },
} as const;

interface ComposerPrivacyToggleProps {
	isPrivate: boolean;
	onToggle: () => void;
	disabled?: boolean;
}

/**
 * Records-tab toggle for private uploads. Only rendered for users who can set
 * private documents. The whole pill is the control; the track/knob is a purely
 * visual switch (no nested interactive element) so the on/off state is obvious.
 * TODO: Revisit a coherent private accent pass across composer tabs, dropzone,
 * records filters/rows and drawers before changing this control's color language.
 */
const ComposerPrivacyToggle: React.FC<ComposerPrivacyToggleProps> = (props) => {
	const { isPrivate, onToggle, disabled } = props;
	const { canSetPrivateDocuments } = useHousePermissions();
	const { isSuperAdmin } = useAuth();

	if (!canSetPrivateDocuments && !isSuperAdmin) return null;

	const preserveEditorFocus = (event: React.PointerEvent<HTMLButtonElement>) => {
		if (!disabled) event.preventDefault();
	};

	return (
		<Tooltip
			openDelay={COMPOSER_TOOLTIP_OPEN_DELAY_MS}
			closeDelay={COMPOSER_TOOLTIP_CLOSE_DELAY_MS}
			content={isPrivate ? 'Available only to Owners and Admins' : 'Available to everyone with access'}
			positioning={{ placement: 'top' }}
		>
			<Button
				type="button"
				onPointerDown={preserveEditorFocus}
				onClick={onToggle}
				disabled={disabled}
				aria-label="Private"
				aria-pressed={isPrivate}
				variant="ghost"
				size="sm"
				h={COMPOSER_BAR_CONTROL_H}
				minH={COMPOSER_BAR_CONTROL_H}
				rounded="full"
				px={3}
				gap={2}
				flexShrink={0}
				textStyle="body.sm"
				fontWeight={isPrivate ? 'medium' : 'normal'}
				color={isPrivate ? 'brand.fg' : 'fg.muted'}
				_hover={{ bg: isPrivate ? 'brand.100' : 'surface.subtle' }}
				css={PRIVACY_PILL_CSS}
			>
				<Icon as={isPrivate ? LuLock : LuLockOpen} boxSize={4} />
				<Text as="span" css={PRIVACY_LABEL_CSS}>
					Private
				</Text>
				<Box
					as="span"
					position="relative"
					w={8}
					h={4.5}
					borderRadius="full"
					flexShrink={0}
					bg={isPrivate ? 'brand.solid' : 'brand.emphasized'}
					transition="background-color 0.2s ease"
				>
					<Box
						as="span"
						position="absolute"
						top="2px"
						left="2px"
						w={3.5}
						h={3.5}
						borderRadius="full"
						bg="white"
						boxShadow="sm"
						transform={isPrivate ? 'translateX(14px)' : 'translateX(0)'}
						transition="transform 0.2s ease"
					/>
				</Box>
			</Button>
		</Tooltip>
	);
};

export default ComposerPrivacyToggle;
